import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  Link,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { NotificationsActiveOutlined } from "@mui/icons-material";
import { useToast } from "components/Toast";
import { isFinancialManager } from "utils/roles";
import {
  getPermissionAsync,
  isIosSafari,
  isIphone,
  isNativePushSupported,
  isPushSupported,
  isStandaloneDisplay,
  subscribeToPush,
  PushPermissionError,
} from "./pushClient";
import { openAppStore } from "../native/appStore";
import { openIphoneSettings } from "../native/iosSettings";

// The ask comes once, on the first open after login; "Not now" brings it
// back every three days (Mostafa, 2026-10-22 — it used to return daily).
// Stored as the time of the last Not now, device-local. Older installs hold
// the previous format, a YYYY-MM-DD day; that counts as a Not now at the
// start of that day.
export const NOT_NOW_KEY = "phieg.pwa.notificationGateNotNowDay";
export const SNOOZE_DAYS = 3;
const SNOOZE_MS = SNOOZE_DAYS * 24 * 60 * 60 * 1000;

export const BLOCKED_LINE =
  "Your browser blocked notifications — turn them on in its site settings, then Settings → Notifications here.";

// When "Not now" was last tapped, as a timestamp; null when never.
function lastNotNowAt() {
  try {
    const raw = localStorage.getItem(NOT_NOW_KEY);
    if (!raw) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-").map(Number);
      return new Date(y, m - 1, d).getTime();
    }
    const at = Number(raw);
    return Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
}

function snoozed(now = Date.now()) {
  const at = lastNotNowAt();
  return at !== null && now - at < SNOOZE_MS;
}

function snooze(now = Date.now()) {
  try {
    localStorage.setItem(NOT_NOW_KEY, String(now));
  } catch {
    /* private mode / blocked storage — the sheet simply returns next open */
  }
}

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// An iPhone in a browser tab: the store app is where notifications live now,
// so the sheet sends them to the App Store instead of asking the browser.
const iphoneNeedsApp = () => isIphone() && !isStandaloneDisplay();
// An iPad in a browser tab: the store app is iPhone-only, so the iPad keeps
// the Home Screen web app. Safari only exposes PushManager to a site that
// was installed to the Home Screen and launched from there, so
// isPushSupported() is false before the install; that case gets the install
// guide, not silence.
const ipadNeedsInstall = () => isIosSafari() && !isIphone() && !isStandaloneDisplay();

/**
 * First-open notification prompt: a bottom sheet that turns push on with ONE
 * tap. Browsers only show their permission dialog from a user gesture, so the
 * tap on "Turn on notifications" IS the gesture — subscribeToPush asks the
 * browser, subscribes the service worker and saves the subscription on the
 * server in that same call; nothing is deferred to Settings.
 *
 * Shows only when it can do that: a logged-in, non-impersonated session (an
 * admin logged in as a client must never bind their own browser to that
 * client), not a Financial Manager (no lead pushes), push not deliberately
 * switched off in Settings (pushEnabled === false is an answer already given),
 * push supported here, permission still "default" (never asked), and no
 * "Not now" in the last three days. An iPhone in a browser gets the App
 * Store pitch, an iPad the Home Screen install guide, instead of the button.
 * Inside the iOS store app the same tap asks iOS instead of the browser
 * (pushClient delegates to native/push.js); the Android shell has no channel
 * at all, so the gate stays silent there.
 *
 * Users who already answered the device (granted or denied) never see this;
 * the reconnect banner in InstallPrompt and the Settings toggle cover them.
 */
export default function NotificationGate() {
  const token = useSelector((s) => s.auth?.token);
  const user = useSelector((s) => s.auth?.user);
  const impersonator = useSelector((s) => s.auth?.impersonator);
  const toast = useToast();
  const theme = useTheme();
  const surface = theme.palette.extras.surface;
  const isPhone = useMediaQuery("(max-width:600px)");

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const eligible =
    Boolean(token && user) &&
    !impersonator &&
    !isFinancialManager(user) &&
    (isNativePushSupported() || user?.pushEnabled !== false);

  useEffect(() => {
    if (!eligible) {
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      if (isNativePushSupported()) {
        // Store app: iOS was asked at first open (usePushAutoResubscribe) —
        // the only thing left to say is where to allow it after a refusal.
        if ((await getPermissionAsync()) !== "denied") return;
      } else if (!iphoneNeedsApp() && !ipadNeedsInstall()) {
        if (!isPushSupported()) return;
        // Async: inside the iOS app the answer comes from the plugin.
        if ((await getPermissionAsync()) !== "default") return;
      }
      if (snoozed()) return;
      if (!cancelled) setOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [eligible]);

  const appStorePitch = iphoneNeedsApp();
  const iosGuide = !appStorePitch && ipadNeedsInstall();
  // Store app: the sheet only ever opens after a refusal (effect above).
  const storeAppDenied = !appStorePitch && !iosGuide && isNativePushSupported();

  const handleTurnOn = async () => {
    setBusy(true);
    try {
      await subscribeToPush(token);
      toast.success("Notifications are on.");
      setOpen(false);
    } catch (error) {
      setBusy(false);
      if (error instanceof PushPermissionError) {
        if (error.permission === "denied") {
          // The browser now blocks us until the user flips it in the site
          // settings; nothing more the sheet can do, so say so and leave.
          toast.warning(BLOCKED_LINE, { duration: 10000 });
          setOpen(false);
        }
        // "default" again = the browser dialog was closed without an answer.
        // Keep the sheet: they can tap again or choose Not now.
        return;
      }
      toast.error(error?.message || "Could not turn on notifications.");
    }
  };

  const handleNotNow = () => {
    snooze();
    setOpen(false);
  };

  // Off to the App Store; the sheet comes back in three days if they did
  // not install (the store app never shows it — it asks iOS instead).
  const handleGetApp = () => {
    openAppStore();
    snooze();
    setOpen(false);
  };

  // Store app after a refusal: straight to Phieg's page in iPhone Settings;
  // the sheet comes back in three days if notifications are still off.
  const handleOpenSettings = () => {
    openIphoneSettings();
    snooze();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Dialog
      open
      onClose={handleNotNow}
      TransitionComponent={isPhone ? SlideUp : undefined}
      aria-labelledby="notification-gate-title"
      aria-describedby="notification-gate-text"
      PaperProps={{
        sx: {
          // Phone: a sheet pinned to the bottom edge (same shape as
          // MobileFilterSheet). Desktop: a centred card. Colors are explicit
          // dark-surface tokens — an unstyled Dialog paper resolves white here.
          ...(isPhone
            ? {
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                width: "100%",
                maxWidth: "100%",
                borderRadius: "18px 18px 0 0",
                borderBottom: "none",
              }
            : {
                width: "100%",
                maxWidth: 440,
                borderRadius: `${theme.palette.extras.radius.xl}px`,
              }),
          m: 0,
          border: `1px solid ${surface.borderSubtle}`,
          backgroundColor: surface.base,
          backgroundImage: "none",
          color: surface.textHigh,
        },
      }}
    >
      {isPhone ? (
        <Box
          aria-hidden
          sx={{
            width: 36,
            height: 4,
            borderRadius: 999,
            backgroundColor: surface.borderDefault,
            mx: "auto",
            mt: 1,
          }}
        />
      ) : null}

      <Stack
        spacing={2}
        sx={{
          p: { xs: "1rem 1.25rem", sm: 3 },
          pb: {
            xs: "calc(20px + var(--safe-area-bottom, 0px))",
            sm: 3,
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <NotificationsActiveOutlined
            sx={{ color: theme.palette.extras.accent.main, fontSize: 28 }}
          />
          <Typography
            id="notification-gate-title"
            component="h2"
            variant="h6"
            sx={{ fontWeight: 700, color: surface.textHigh, lineHeight: 1.25 }}
          >
            {appStorePitch
              ? "Get the Phieg app"
              : iosGuide
              ? "Get lead alerts on this iPad"
              : storeAppDenied
              ? "Lead alerts are off"
              : "Turn on lead alerts"}
          </Typography>
        </Stack>

        {appStorePitch ? (
          <Typography
            id="notification-gate-text"
            variant="body2"
            sx={{ color: surface.textMid }}
          >
            Phieg is on the App Store now. Install it and sign in to get a
            notification the moment a lead is assigned to you.
          </Typography>
        ) : iosGuide ? (
          <Typography
            id="notification-gate-text"
            variant="body2"
            sx={{ color: surface.textMid }}
          >
            A notification the moment a lead is assigned to you. On iPad,
            add Phieg CRM to your Home Screen first: tap <strong>Share</strong>{" "}
            → <strong>Add to Home Screen</strong>, then open the app from the
            Home Screen icon and turn notifications on there.{" "}
            <Link
              component={RouterLink}
              to="/get-app"
              onClick={handleNotNow}
              sx={{ color: theme.palette.extras.accent.soft, fontWeight: 600 }}
            >
              How to install
            </Link>
          </Typography>
        ) : storeAppDenied ? (
          <Typography
            id="notification-gate-text"
            variant="body2"
            sx={{ color: surface.textMid }}
          >
            Notifications are turned off for Phieg on this iPhone, so a lead
            assigned to you can&apos;t reach you here. Turn them on in{" "}
            <strong>iPhone Settings</strong> → <strong>Notifications</strong> →{" "}
            <strong>Phieg</strong>.
          </Typography>
        ) : (
          <Typography
            id="notification-gate-text"
            variant="body2"
            sx={{ color: surface.textMid }}
          >
            A notification the moment a lead is assigned to you — so you can
            call while the lead is still warm.
          </Typography>
        )}

        {appStorePitch ? (
          <Stack spacing={1}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleGetApp}
              sx={{ fontWeight: 700 }}
            >
              Get it on the App Store
            </Button>
            <Button
              variant="text"
              size="large"
              fullWidth
              onClick={handleNotNow}
              sx={{ color: surface.textMid }}
            >
              Not now
            </Button>
          </Stack>
        ) : iosGuide ? (
          <Button variant="outlined" size="large" fullWidth onClick={handleNotNow}>
            Not now
          </Button>
        ) : storeAppDenied ? (
          <Stack spacing={1}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleOpenSettings}
              sx={{ fontWeight: 700 }}
            >
              Open iPhone Settings
            </Button>
            <Button
              variant="text"
              size="large"
              fullWidth
              onClick={handleNotNow}
              sx={{ color: surface.textMid }}
            >
              Not now
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={busy}
              onClick={handleTurnOn}
              sx={{ fontWeight: 700 }}
            >
              {busy ? "Turning on…" : "Turn on notifications"}
            </Button>
            <Button
              variant="text"
              size="large"
              fullWidth
              disabled={busy}
              onClick={handleNotNow}
              sx={{ color: surface.textMid }}
            >
              Not now
            </Button>
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
}
